import { memo, useCallback, useState } from 'react';
import { Form, FormProps, Collapse, Row, Col, Flex, CollapseProps } from 'antd';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import { MSkeleton } from '@Components/Base';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as NextArrowIcon } from '@Icons/additional/arrow-left.svg';
import { ReactComponent as SearchIcon } from '@Icons/additional/search-gradient.svg';
import { ReactComponent as ArrowDownIcon } from '@Icons/additional/chevron-down-gradient.svg';

import { createdTender } from '@Features/MaterialRequest/requests/PurchaseRequest/store/selectors/purchaseRequestSelector';

import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { useHeaderCaption } from '@Hooks/useHeaderCaption';
import { PurchaseFormResponse, PurchaseItemResponse } from '@Types/api/form';
import { RequestListSearchResponse } from '@Types/api/requestList';
import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import { FooterActions } from '@Enum/index';
import ViewDetailesModal from '@Features/MaterialRequest/requests/components/ViewDetailesModal/ViewDetailesModal';
import RequisitionInformation from '../components/RequisitionInformation/RequisitionInformation';
import FooterButtonSection from '../components/FooterButtonSection/FooterButtonSection';
import OverviewButton from '../components/OverviewButton/OverviewButton';
import { OverviewMaterials } from '../components/OverviewMaterials';

import styles from './create-tender-desktop.module.css';

// import { NotificationContext } from '@Context/NotificationContext';

import CollapseItems from '../components/CollapseItems/CollapseItems';

import { purchaseRequestActions } from '../../../store/purchaseRequestSlice';

const expandIcon = ({ isActive }: { isActive?: boolean }): JSX.Element => (
  <ArrowDownIcon
    className={classNames(styles.expandIcon, { [styles.active]: isActive })}
  />
);

interface CreateTenderDesktopProps {
  listRequests: RequestListSearchResponse<PurchaseFormResponse>;
  requestsDataList: PurchaseFormResponse[];
  purchaseItems: Record<number, PurchaseItemResponse[]>;
  overviewItems: PurchaseItemResponse[];
  selectedOverviewItems: PurchaseItemResponse[];
  onGetPurchaseItems: (requestId: number) => void;
  handleCreateTender: () => void;
  loadMore: () => void;
  handleReviewTender: () => void;
  className?: string;
  layout?: FormProps['layout'];
}

const CreateTenderDesktop: React.FC<CreateTenderDesktopProps> = ({
  listRequests,
  requestsDataList,
  purchaseItems,
  overviewItems,
  selectedOverviewItems,
  className,
  layout = 'vertical',
  onGetPurchaseItems,
  handleCreateTender,
  loadMore,
  handleReviewTender,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fetchedSectionsKeys, setFetchedSectionsKeys] = useState<string[]>([]);
  const tenderId = useAppSelector(createdTender);
  const [isViewDetailesOpen, setIsViewDetailesOpen] = useState(false);
  const [purchaseDetails, setPurchaseDetails] =
    useState<PurchaseFormResponse>();
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);

  const headerCaption = useHeaderCaption(false);

  const handleBack = (): void => {
    dispatch(purchaseRequestActions.clearBasket());
    navigate(
      `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}`
    );
  };

  const handleSearch = (): void => {
    // eslint-disable-next-line
    alert('handleSearch');
  };

  const handleAddMaterial = (purchaseItem: PurchaseItemResponse): void => {
    dispatch(purchaseRequestActions.addOverviewItem(purchaseItem));
    dispatch(purchaseRequestActions.setSelectedOverviewItem(purchaseItem));
  };
  const handleRemoveMaterial = (purchaseItem: PurchaseItemResponse): void => {
    dispatch(purchaseRequestActions.removeOverviewItem(purchaseItem));
  };

  const handleRemoveAllMaterial = (): void => {
    dispatch(purchaseRequestActions.removeAllOverviewItem());
  };

  const handleViewDetailes = (purchaseItem: PurchaseFormResponse): void => {
    setPurchaseDetails(purchaseItem);
    setIsViewDetailesOpen(!isViewDetailesOpen);
  };

  const handleCloseViewDetailes = (): void => {
    setIsViewDetailesOpen(!isViewDetailesOpen);
  };

  const handleSelectOverviewItem = (
    purchaseItem: PurchaseItemResponse
  ): void => {
    dispatch(purchaseRequestActions.setSelectedOverviewItem(purchaseItem));
  };

  const handleUnselectOverviewItem = (
    purchaseItem: PurchaseItemResponse
  ): void => {
    dispatch(purchaseRequestActions.setUnselectedOverviewItem(purchaseItem));
  };

  const handleSelectAllOverviewItems = (): void => {
    dispatch(purchaseRequestActions.selectAllOverviewItems());
  };

  const handleUnselectAllOverviewItems = (): void => {
    dispatch(purchaseRequestActions.unselectAllOverviewItems());
  };

  const handleCollapse: CollapseProps['onChange'] = (key): void => {
    if (key && key.length > 0) {
      const newSectionKey = key.find((k) => !fetchedSectionsKeys.includes(k));

      if (newSectionKey) {
        const sectionId = Number(newSectionKey);
        onGetPurchaseItems(sectionId);
      }

      setFetchedSectionsKeys(key);
    }
  };

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleCreateTender();
      }

      if (action === FooterActions.REVIEW_AND_CONFIRM) {
        handleReviewTender();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        dispatch(purchaseRequestActions.setCreatedTenderId(0));
        navigate(
          `/mr/${MRFeatureRoutePath.PROCUREMENT}/${MRFeatureRoutePath.PROCUREMENT_PURCHASE_REQUEST}`
        );
      }
    },
    [handleCreateTender, handleReviewTender]
  );

  return (
    <div>
      <div className={styles.formWrapper}>
        <div className={styles.formBackSection}>
          <NextArrowIcon onClick={handleBack} />
          <span>{headerCaption}</span>
        </div>
        <Row className={styles.header} align="middle">
          <Col className={styles.requisitionContainer} span={16}>
            <RequisitionInformation
              requisitionNo={String(tenderId)}
              requisitionDate={dayjs().format('DD/MM/YYYY')}
              requisitionStatus="New"
            />
          </Col>
          <Col span={8}>
            <Flex justify="flex-end">
              <OverviewButton
                count={overviewItems.length}
                onclick={() => setIsOverviewOpen(true)}
              />
            </Flex>
          </Col>
        </Row>
        <Form
          className={classNames(styles.form, className)}
          layout={layout}
          requiredMark={false}
        >
          <Flex className={styles.formContent} vertical gap={24}>
            <Flex className={styles.formHeader} justify="space-between">
              <div>
                <span className={styles.formHeaderTitle}>Create tender</span>
                <span className={styles.formHeaderText}>
                  Select the materials that you need to create this tender
                </span>
              </div>
              <div onClick={handleSearch} className={styles.iconButton}>
                <SearchIcon />
              </div>
            </Flex>
            <div id="scrollableDiv" className={styles.scrollableContainer}>
              <InfiniteScroll
                next={loadMore}
                hasMore={requestsDataList.length < listRequests?.totalElements}
                loader={<MSkeleton paragraph={{ rows: 1 }} active />}
                dataLength={requestsDataList.length}
                scrollableTarget="scrollableDiv"
              >
                <Collapse
                  className={styles.collapseSection}
                  ghost
                  expandIcon={expandIcon}
                  onChange={handleCollapse}
                  items={CollapseItems({
                    requestsDataList,
                    purchaseItems,
                    overviewItems,
                    handleAddMaterial,
                    handleRemoveMaterial,
                    handleViewDetailes,
                  })}
                />
              </InfiniteScroll>
            </div>
          </Flex>
          <div className={classNames(styles.formFooterContent)}>
            <FooterButtonSection
              disabledSaveForm={Boolean(selectedOverviewItems.length)}
              disabledReview={!selectedOverviewItems.length && tenderId === 0}
              onFooterAction={handleFooterAction}
            />
          </div>
        </Form>
      </div>
      <ViewDetailesModal
        purchaseDetails={purchaseDetails}
        isOpen={isViewDetailesOpen}
        onClose={handleCloseViewDetailes}
      />
      <OverviewMaterials
        overviewItems={overviewItems}
        selectedOverviewItems={selectedOverviewItems}
        isOpen={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
        handleAddMaterial={handleAddMaterial}
        handleRemoveMaterial={handleRemoveMaterial}
        handleRemoveAllMaterial={handleRemoveAllMaterial}
        handleSelectOverviewItem={handleSelectOverviewItem}
        handleUnselectOverviewItem={handleUnselectOverviewItem}
        handleSelectAllOverviewItems={handleSelectAllOverviewItems}
        handleUnselectAllOverviewItems={handleUnselectAllOverviewItems}
      />
    </div>
  );
};

export default memo(CreateTenderDesktop);
